import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  nestPortfolioDataPoints,
  sortPortfolioDataPoints,
} from '@products/portfolio/portfolio.utils';
import { EMDASH } from '@products/utils/constants/product.constants';
import {
  PortfolioComponentId,
  PortfolioDTO,
  PortfolioSectorAllocationDTO,
} from '@types';
import { Logger } from '@utils/logger';
import { formatPercentage } from '@utils/text/number-utils';
import groupBy from 'lodash/groupBy';
import { MapperFactory } from '../mapper-factory';
import {
  getFirstNonEmptyProp,
  hasValue,
  parseStdNumber,
  parseYN,
} from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';

const logger = Logger.getLogger('SectorAllocationMapper');

export const isSectorExposure = (
  point: PortfolioSectorAllocationDTO
): boolean =>
  hasValue(point, 'brkdwnpctlong') || hasValue(point, 'brkdwnpctshort');

export const isSectorDuration = (
  point: PortfolioSectorAllocationDTO
): boolean => point.calcnamestd === 'TOTAL_PCT_OF_TOTAL_DC';

export const hasSectorDuration = (
  points: PortfolioSectorAllocationDTO[]
): boolean => points.filter(isSectorDuration).length > 0;

export const createAllocationFilter = (allocationHeadingStd: string) => (
  point: PortfolioSectorAllocationDTO
) => point.allocheadingstd === allocationHeadingStd;

/**
 * example fund (no chart):
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/20225/A/franklin-k-2-long-short-credit-fund/FKLSX#portfolio
 */
export const mapSectorExposure = (
  data: PortfolioSectorAllocationDTO[],
  componentId: PortfolioComponentId,
  baseLabelKey: string
): PortfolioAllocation => {
  const calcParent = (point: PortfolioSectorAllocationDTO): string => {
    // TODO: this logic feels a bit hacky. See if PDS can firm up the data
    if (point.sectorname === point.parentsectorname) {
      // used for "Total"
      return '';
    }
    if (point.sectorname) {
      return point.parentsectorname;
    }
    // parent sectors, have empty sectorname
    return '';
  };

  let dataPoints: PortfolioAllocationDataPoint[] = data
    .sort(sortPortfolioDataPoints)
    .map(
      (point: PortfolioSectorAllocationDTO): PortfolioAllocationDataPoint => {
        return {
          label: point.sectorname || point.parentsectorname, // top levels have sectorname null, so use parentsectorname instead
          parent: calcParent(point),
          longExposure: formatPercentage(point.brkdwnpctlong),
          shortExposure: formatPercentage(point.brkdwnpctshort),
          netExposure: formatPercentage(point.brkdwnpct),
        };
      }
    );

  logger.debug('dataPoints before:', dataPoints);

  dataPoints = nestPortfolioDataPoints(dataPoints);

  logger.debug('dataPoints after:', dataPoints);

  const allocation: PortfolioAllocation = {
    componentId,
    baseLabelKey,
    showExposureChart: false,
    hideChart: true,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calcname'),
    benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    data: dataPoints,
  };
  return allocation;
};

// tslint:disable-next-line:max-line-length
// https://en-us.staging.ftsites.com/investments/options/exchange-traded-funds/products/18000/SINGLCLASS/franklin-liberty-short-duration-u-s-government-etf/FTSD#portfolio
export const mapSectorDuration = (
  data: PortfolioSectorAllocationDTO[],
  componentId: PortfolioComponentId,
  baseLabelKey: string,
  mapperParams: MapperParams
): PortfolioAllocation => {
  logger.debug(
    'sector duration grouped:',
    Object.values(groupBy(data, 'sectorname'))
  );
  const allocation: PortfolioAllocation = {
    componentId,
    caveatComponentId: PortfolioComponentId.SECTOR_ALLOCATION,
    baseLabelKey,
    breakdownColumnTitle: mapperParams.translateService.instant(
      'products.percent-of-total'
    ),
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calcname'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
    data: Object.values(groupBy(data, 'sectorname'))
      // after grouping, we'll get an array with [value, duration] pairs
      // e.g. [[{value}, {duration}], [{duration}, {value}], [{value}, {duration}], ...]
      .map(
        (
          group: PortfolioSectorAllocationDTO[]
        ): PortfolioAllocationDataPoint => {
          // extract the gross and net data points for this category
          const valuePoint: PortfolioSectorAllocationDTO = group.find(
            (point: PortfolioSectorAllocationDTO): boolean =>
              ['TOTAL_PCT_OF_TOTAL_MV', 'TOTAL_PCT_OF_TOTAL_EXP'].includes(
                point.calcnamestd
              )
          );
          const durationPoint: PortfolioSectorAllocationDTO = group.find(
            (point: PortfolioSectorAllocationDTO): boolean =>
              point.calcnamestd === 'TOTAL_PCT_OF_TOTAL_DC'
          );
          // return a single point with breakdown+duration values
          return {
            label: group[0].sectorname,
            breakdown: formatPercentage(valuePoint?.brkdwnpct),
            breakdownStd: parseStdNumber(valuePoint?.brkdwnpctstd),
            duration: durationPoint?.brkdwnpct || EMDASH,
            order: parseStdNumber(valuePoint?.rank),
          };
        }
      )
      // sort the array
      .sort(
        (
          a: PortfolioAllocationDataPoint,
          b: PortfolioAllocationDataPoint
        ): number => a.order - b.order
      ),
  };
  return allocation;
};

/**
 * example fund:
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export const mapSectorAllocation = (
  data: PortfolioSectorAllocationDTO[],
  componentId: PortfolioComponentId,
  baseLabelKey: string
): PortfolioAllocation => ({
  componentId,
  caveatComponentId: PortfolioComponentId.SECTOR_ALLOCATION,
  baseLabelKey,
  allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
  allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
  calculationType: getFirstNonEmptyProp(data, 'calcname'),
  benchmarkName: getFirstNonEmptyProp(data, 'bmname'),
  hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioSectorAllocationDTO): PortfolioAllocationDataPoint => ({
      label: obj.sectorname,
      breakdown: formatPercentage(obj.brkdwnpct),
      breakdownStd: parseStdNumber(obj.brkdwnpctstd),
      benchmark: formatPercentage(obj.bmbrkdwnpct),
      benchmarkStd: parseStdNumber(obj.bmbrkdwnpctstd),
    })
  ),
});

/**
 * Static config class for the different types of Sector Allocations
 * config class based on this https://stackoverflow.com/a/51398471
 */
export class SectorAllocationConfig {
  // private to disallow creating other instances of this type
  private constructor(
    public readonly componentId: PortfolioComponentId,
    // used for labels, caveats etc e.g. "top-ten-holding-money-funds"
    public readonly baseLabelKey: string,
    // used to convert filtered data into a PortfolioAllocation object
    public readonly mapFn: (
      data: PortfolioSectorAllocationDTO[],
      componentId: PortfolioComponentId,
      baseLabelKey: string,
      mapperParams?: MapperParams
    ) => PortfolioAllocation,
    // used to filter data to a specific sector allocation type
    public readonly filterFn?: (point: PortfolioSectorAllocationDTO) => boolean
  ) {}
  // configs

  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
  static readonly SECTOR_ALLOCATION = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_ALLOCATION,
    'sector-allocation',
    mapSectorAllocation
    // no filterFn, as this is the default config to catch any rows that don't pass the other filters
  );
  static readonly SECTOR_DURATION = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_DURATION,
    'sector-duration',
    mapSectorDuration
    //  noFilterFn, as Duration treated as special case
  );
  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/exchange-traded-funds/products/18000/SINGLCLASS/franklin-liberty-short-duration-u-s-government-etf/FTSD#portfolio
  static readonly SECTOR_EMD = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_EMD,
    'sector-emd',
    mapSectorAllocation,
    createAllocationFilter('EMD Sector Allocation')
  );
  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/separately-managed-accounts/products/CO8420/NA/franklin-income-sma#portfolio
  static readonly SECTOR_EQUITY = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_EQUITY,
    'sector-equity',
    mapSectorAllocation,
    createAllocationFilter('Sector Allocation—Equity')
  );
  // example fund (no chart):
  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/20225/A/franklin-k-2-long-short-credit-fund/FKLSX#portfolio
  static readonly SECTOR_EXPOSURE = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_EXPOSURE,
    'sector-exposure',
    mapSectorExposure,
    isSectorExposure
  );
  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/separately-managed-accounts/products/CO8420/NA/franklin-income-sma#portfolio
  static readonly SECTOR_FIXED_INCOME = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_FIXED_INCOME,
    'sector-fixed-income',
    mapSectorAllocation,
    createAllocationFilter('Sector Allocation—Fixed Income')
  );
  static readonly SECTOR_HIGH_YIELD = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_HIGH_YIELD,
    'sector-high-yield',
    mapSectorAllocation,
    createAllocationFilter('High Yield Sector Allocation')
  );
  static readonly SECTOR_MBS = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_MBS,
    'sector-mbs',
    mapSectorAllocation,
    createAllocationFilter('MBS Sector Allocation')
  );
  static readonly SECTOR_INVESTMENT_GRADE = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_INVESTMENT_GRADE,
    'sector-investment-grade',
    mapSectorAllocation,
    createAllocationFilter('Investment Grade Sector Allocation')
  );

  static allConfigs: SectorAllocationConfig[] = [
    SectorAllocationConfig.SECTOR_ALLOCATION,
    SectorAllocationConfig.SECTOR_DURATION,
    SectorAllocationConfig.SECTOR_EMD,
    SectorAllocationConfig.SECTOR_EQUITY,
    SectorAllocationConfig.SECTOR_EXPOSURE,
    SectorAllocationConfig.SECTOR_FIXED_INCOME,
    SectorAllocationConfig.SECTOR_HIGH_YIELD,
    SectorAllocationConfig.SECTOR_INVESTMENT_GRADE,
    SectorAllocationConfig.SECTOR_MBS,
  ];

  // returns matching config or undefined
  static getConfig(componentId: PortfolioComponentId): SectorAllocationConfig {
    return this.allConfigs.find(
      (config: SectorAllocationConfig): boolean =>
        config.componentId === componentId
    ); // just match on key
  }

  // e.g. console.log(SectorAllocationConfig.SECTOR_ALLOCATION) prints 'SECTOR_ALLOCATION'
  toString() {
    return this.componentId;
  }
}

export interface AllocationBucket {
  config: SectorAllocationConfig;
  data: PortfolioSectorAllocationDTO[];
}

export const reduceSectorData = (
  buckets: { [id: string]: AllocationBucket },
  point: PortfolioSectorAllocationDTO
): { [id: string]: AllocationBucket } => {
  // 1. see which allocation type filter passes for this row
  let config: SectorAllocationConfig = SectorAllocationConfig.allConfigs.find(
    (c: SectorAllocationConfig): boolean => c.filterFn && c.filterFn(point)
  );
  if (!config) {
    // set to default config if it doesn't pass any filters
    config = SectorAllocationConfig.SECTOR_ALLOCATION;
  }

  // 2. create new bucket if it doesn't already exist
  if (!buckets[config.componentId]) {
    buckets[config.componentId] = { config, data: [] };
  }

  // 3. add row to bucket
  buckets[config.componentId].data.push(point);

  return buckets;
};

export const mapSectorBuckets = (
  bucket: AllocationBucket
): PortfolioAllocation =>
  bucket.config.mapFn(
    bucket.data,
    bucket.config.componentId,
    bucket.config.baseLabelKey
  );

export class SectorAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioSectorAllocationDTO[] = portfolioDto.sectorallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    // treat Sector Duration as a special case:
    // if ANY rows have calcnamestd === 'TOTAL_PCT_OF_TOTAL_DC', treat ALL rows as Sector Duration chart
    if (hasSectorDuration(data)) {
      return [
        mapSectorDuration(
          data,
          SectorAllocationConfig.SECTOR_DURATION.componentId,
          SectorAllocationConfig.SECTOR_DURATION.baseLabelKey,
          this.mapperParams
        ),
      ];
    }

    // 1. split data into buckets depending on Sector Allocation type
    // sometimes the data can contain rows for multiple sector charts
    // e.g. Sector Equity + Sector Fixed Income charts both displayed for one fund
    // Most of the time, there will just be one Sector Allocation chart
    const buckets: {
      [id: string]: AllocationBucket;
    } = data.reduce(reduceSectorData, {});
    logger.debug('sector buckets', buckets);

    // 2. map buckets into allocation objects
    const allocations: PortfolioAllocation[] = Object.values(buckets).map(
      mapSectorBuckets
    );
    logger.debug('sector allocations', allocations);

    return allocations;
  }
}
